import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  Grid,
  Collapse,
  FormControl,
} from "@mui/material";
import { CalendarToday } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  getUserDateFormat,
  convertToDateFnsFormat,
} from "../../../utils/formatDate";

const DateTabs = ({ onDateRangeChange, selectedDateRange = "today" }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [dateError, setDateError] = useState("");

  // Get user date format
  const userDateFormat = getUserDateFormat();
  const getDatePlaceholder = () => userDateFormat;
  const getDateInputFormat = () => convertToDateFnsFormat(userDateFormat);

  // Define date range options
  const dateRangeOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This Week", value: "last 7 days" },
    { label: "Custom", value: "custom" },
  ];

  // Find the active tab index based on selected date range
  React.useEffect(() => {
    const index = dateRangeOptions.findIndex(
      (option) => option.value === selectedDateRange
    );
    if (index !== -1) {
      setActiveTab(index);
      setShowCustomDatePicker(selectedDateRange === "custom");
    }
  }, [selectedDateRange]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const selectedOption = dateRangeOptions[newValue];

    if (selectedOption.value === "custom") {
      setShowCustomDatePicker(true);
      setDateError("");
      // Initialize with today's date if not set
      if (!customStartDate || !customEndDate) {
        const today = new Date();
        setCustomStartDate(today);
        setCustomEndDate(today);
      }
    } else {
      setShowCustomDatePicker(false);
      setDateError("");
      // Calculate date range based on selection
      const dateRange = calculateDateRange(selectedOption.value);
      if (onDateRangeChange) {
        onDateRangeChange(selectedOption.value, dateRange);
      }
    }
  };

  const calculateDateRange = (rangeType) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (rangeType) {
      case "today":
        return {
          startDate: today,
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
        };

      case "yesterday":
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        return {
          startDate: yesterday,
          endDate: new Date(yesterday.getTime() + 24 * 60 * 60 * 1000 - 1),
        };

      case "this_week":
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        return {
          startDate: startOfWeek,
          endDate: endOfWeek,
        };

      default:
        return {
          startDate: today,
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
        };
    }
  };

  const handleCustomDateSubmit = () => {
    setDateError("");

    if (!customStartDate || !customEndDate) {
      setDateError("Please select both start and end dates");
      return;
    }

    const startDate = new Date(customStartDate);
    const endDate = new Date(customEndDate);
    endDate.setHours(23, 59, 59, 999);

    if (startDate > endDate) {
      setDateError("Start date must be before or equal to end date");
      return;
    }

    // Calculate the difference in days
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 30) {
      setDateError("Date range cannot exceed 30 days");
      return;
    }

    const dateRange = {
      startDate,
      endDate,
    };

    if (onDateRangeChange) {
      onDateRangeChange("custom", dateRange);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ mt: 2, mb: 3 }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px 8px 0 0",
            px: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            TabIndicatorProps={{
              style: {
                backgroundColor: "#000",
                height: "1px",
              },
            }}
            sx={{
              "& .MuiTab-root": {
                minWidth: 120,
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "14px",
                position: "relative",
                color: "#000",
              },
              "& .Mui-selected": {
                backgroundColor: "#03A9F4",
                color: "#fff !important",
                borderTopLeftRadius: "4px",
                borderTopRightRadius: "4px",
              },
            }}
          >
            {dateRangeOptions.map((option, index) => (
              <Tab
                key={index}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {option.value === "custom" && (
                      <CalendarToday sx={{ fontSize: "16px" }} />
                    )}
                    {option.label}
                  </Box>
                }
                id={`date-tab-${index}`}
                aria-controls={`date-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {/* Custom Date Range Dropdown */}
        <Collapse in={showCustomDatePicker}>
          <Box
            sx={{
              p: 3,
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderTop: "none",
              borderRadius: "0 0 8px 8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <DatePicker
                    label="Start Date"
                    value={customStartDate}
                    onChange={(newValue) => {
                      setCustomStartDate(newValue);
                      setDateError("");
                    }}
                    format={getDateInputFormat()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        error: !!dateError,
                        placeholder: getDatePlaceholder(),
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "16px",
                            "& fieldset": {
                              borderColor: "#183059",
                              borderWidth: "2px",
                            },
                            "&:hover fieldset": {
                              borderColor: "#03A9F4",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#03A9F4",
                            },
                            "& input": {
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: "16px",
                              color: "#111214",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#03A9F4",
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <DatePicker
                    label="End Date"
                    value={customEndDate}
                    onChange={(newValue) => {
                      setCustomEndDate(newValue);
                      setDateError("");
                    }}
                    format={getDateInputFormat()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        error: !!dateError,
                        placeholder: getDatePlaceholder(),
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "16px",
                            "& fieldset": {
                              borderColor: "#183059",
                              borderWidth: "2px",
                            },
                            "&:hover fieldset": {
                              borderColor: "#03A9F4",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#03A9F4",
                            },
                            "& input": {
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: "16px",
                              color: "#111214",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#03A9F4",
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  onClick={handleCustomDateSubmit}
                  variant="contained"
                  fullWidth
                  startIcon={<CalendarToday />}
                  sx={{
                    backgroundColor: "#03A9F4",
                    "&:hover": {
                      backgroundColor: "#0288d1",
                    },
                    fontWeight: "600",
                    textTransform: "none",
                  }}
                >
                  Apply Range
                </Button>
              </Grid>
              {dateError && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      color: "#d32f2f",
                      backgroundColor: "#ffebee",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      fontSize: "14px",
                      border: "1px solid #ef9a9a",
                    }}
                  >
                    {dateError}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Collapse>
      </Box>
    </LocalizationProvider>
  );
};

export default DateTabs;
